import React from "react";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiBell,
  FiCheckCircle,
  FiClock,
  FiGrid,
  FiMail,
  FiMapPin,
  FiMenu,
  FiMonitor,
  FiNavigation,
  FiPhone,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import logo from "../../assets/images/logo.png";
import heroFeatureImage from "../../assets/images/herofeature.jpg";
import heroClientImage from "../../assets/images/heroclient.jpg";
import schoolImage1 from "../../assets/images/school-image1.jpg";
import dashboardTracking from "../../assets/images/dashboard-tracking-image.jpg";
import controlRoom from "../../assets/images/control-room-image.jpg";
import childrenBus from "../../assets/images/children-bus-image.jpg";
import parentNotification from "../../assets/images/parent-notification-image.jpg";
import landingImage from "../../assets/images/landing-image.png";
import schoolBusImage from "../../assets/images/png/school.png";
import schoolBusWide from "../../modules/static/png/school.png";
import lewooInfographic from "../../assets/images/lewoo-infographic-image.png";
import { Link } from "react-router-dom";

const metrics = [
  { value: "99.9%", label: "Disponibilite plateforme" },
  { value: "< 3s", label: "Delai moyen des alertes" },
  { value: "500+", label: "Bus supervises" },
  { value: "15K+", label: "Eleves suivis" },
];

const features = [
  {
    title: "Suivi GPS en temps reel",
    description: "Localisez chaque bus, visualisez l'itineraire et l'heure d'arrivee avec une carte live.",
    icon: <FiMapPin />,
  },
  {
    title: "Gestion des lignes",
    description: "Planifiez les circuits, les arrets et les affectations de bus depuis une interface unique.",
    icon: <FiNavigation />,
  },
  {
    title: "Suivi des eleves",
    description: "Associez les eleves a leur ligne et suivez les montees et descentes par arret.",
    icon: <FiUsers />,
  },
  {
    title: "Alertes intelligentes",
    description: "Recevez des alertes pour exces de vitesse, sortie d'itineraire, retards ou incidents.",
    icon: <FiAlertTriangle />,
  },
  {
    title: "Notifications parents",
    description: "Les parents recoivent des notifications quand leur enfant monte, descend ou en cas de retard.",
    icon: <FiBell />,
  },
  {
    title: "Zones de surveillance",
    description: "Definissez des geozones autour des ecoles avec limitation de vitesse et detection d'entree/sortie.",
    icon: <FiShield />,
  },
  {
    title: "Salle de controle",
    description: "Supervision globale de la flotte avec analyse des alertes et anomalies en temps reel.",
    icon: <FiMonitor />,
  },
];

const steps = [
  {
    number: "01",
    title: "Installation du dispositif",
    description: "Un boitier GPS discret est installe dans chaque bus scolaire en moins de 30 minutes.",
    image: dashboardTracking,
  },
  {
    number: "02",
    title: "Configuration de la plateforme",
    description: "Les lignes, arrets, chauffeurs et ecoles sont configures dans le tableau de bord central.",
    image: controlRoom,
  },
  {
    number: "03",
    title: "Inscription des eleves",
    description: "Chaque eleve est enregistre et associe a sa ligne de bus avec badge NFC.",
    image: childrenBus,
  },
  {
    number: "04",
    title: "Suivi en temps reel",
    description: "Parents, ecoles et operateurs suivent les bus en direct depuis leur interface dediee.",
    image: parentNotification,
  },
];

const parentBenefits = [
  "Notification a la montee et descente",
  "Heure d'arrivee estimee en temps reel",
  "Alertes de securite immediates",
  "Historique des trajets consultable",
];

const testimonials = [
  {
    name: "Marie Nkoulou",
    role: "Directrice d'ecole, Yaounde",
    quote: "Lewoo Watch Kids a transforme notre gestion du transport. Les parents sont rassures et nous avons reduit les retards de 80%.",
  },
  {
    name: "Jean-Pierre Fotso",
    role: "Responsable flotte, Douala",
    quote: "La salle de controle nous offre une visibilite totale. Les alertes en temps reel nous permettent d'agir immediatement.",
  },
  {
    name: "Amina Bello",
    role: "Parent d'eleve, Bamenda",
    quote: "Je recois une notification des que mon fils monte dans le bus. C'est une tranquillite d'esprit inestimable.",
  },
];

const partners = [
  "Ministere des Transports",
  "UNICEF Cameroun",
  "Mairie de Yaounde",
  "Groupe Scolaire La Gaiete",
  "Lycee Bilingue de Douala",
];

const navItems = [
  { label: "Accueil", href: "#accueil" },
  { label: "Fonctionnalites", href: "#fonctionnalites" },
  { label: "Comment ca marche", href: "#comment-ca-marche" },
  { label: "Infographie", href: "#infographie" },
  { label: "Contact", href: "#contact" },
];

function SectionTag({ children, dark }) {
  return (
    <span
      className={`inline-flex rounded-md px-4 py-2 text-xs font-black uppercase tracking-[0.28em] ${
        dark ? "bg-[#d9a221] text-[#07111b]" : "bg-[#1f5d3f] text-white"
      }`}
    >
      {children}
    </span>
  );
}

function SectionHeading({ title, subtitle, light }) {
  return (
    <div className="mx-auto max-w-5xl text-center">
      <h2 className={`text-4xl font-black leading-tight md:text-6xl ${light ? "text-white" : "text-[#111a24]"}`}>
        {title}
      </h2>
      {subtitle ? (
        <p className={`mx-auto mt-6 max-w-3xl text-lg leading-8 ${light ? "text-white/70" : "text-[#55657a]"}`}>
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function FeatureCard({ icon, title, description, highlighted }) {
  return (
    <div
      className={`rounded-[26px] border bg-white p-9 transition ${
        highlighted ? "border-[#4e7b63] shadow-[0_12px_30px_rgba(25,54,41,0.08)]" : "border-[#d6d9dd]"
      }`}
    >
      <div className="mb-7 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-[#1f5d3f] text-4xl text-white">
        {icon}
      </div>
      <h3 className="text-[2rem] font-black leading-tight text-[#07111b]">{title}</h3>
      <p className="mt-5 text-lg leading-9 text-[#55657a]">{description}</p>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-[#f3f3f1] text-[#07111b]" style={{zoom:0.8}}>
      <section
        id="accueil"
        className="relative overflow-hidden bg-[#06111d] text-white"
        style={{
          backgroundImage: `linear-gradient(90deg,rgba(3,10,18,0.96) 0%, rgba(3,10,18,0.84) 36%,rgba(3,10,18,0.52) 62%,rgba(3,10,18,0.74) 100%),
          url(${schoolImage1})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="px-5 pb-16 pt-10 md:px-10 md:pb-24">
          <div className="max-w-[900px]">
            <h1 className="text-[2.4rem] font-black leading-[0.95] tracking-[-0.04em] md:text-[5.6rem]">
              La securite de vos
              <br />
              enfants, <span className="text-[#d9a221]">en temps reel.</span>
            </h1>
            <p className="mt-8 max-w-[980px] text-xl leading-[1.9] text-white/72 md:text-[1.1rem]">
              Lewoo Watch Kids est la plateforme nationale de suivi du transport scolaire. GPS en temps reel, alertes
              instantanees et notifications pour les parents, deployee dans les 10 regions du Cameroun.
            </p>

            <div className="mt-12 flex flex-wrap gap-5">
              <Link to="/landing-2"  className="inline-flex items-center gap-3 rounded-2xl bg-[#d9a221] px-10 py-5 text-lg font-black uppercase tracking-[0.03em] text-[#07111b]">
             
                Demander une demo <FiArrowRight />
              </Link>
              <a
                href="#fonctionnalites"
                className="inline-flex items-center rounded-2xl border border-white/20 bg-white/5 px-10 py-5 text-lg font-bold text-white"
              >
                Decouvrir la plateforme
              </a>
            </div>
          </div>

          <div className="mt-20 grid gap-0 border-t border-white/12 md:grid-cols-4">
            {metrics.map((metric) => (
              <div key={metric.label} className="border-r border-white/12 px-6 py-10 last:border-r-0 md:px-8">
                <div className="text-5xl font-black text-[#d9a221]">{metric.value}</div>
                <div className="mt-3 text-sm uppercase tracking-[0.24em] text-white/60">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="fonctionnalites" className="px-5 py-24 md:px-10 md:py-28">
        <div className="mx-auto ">
          <div className="text-center">
            <SectionTag>Fonctionnalites</SectionTag>
            <div className="mt-8">
              <SectionHeading
                title="Une solution complète pour le transport scolaire"
                subtitle="7 modules intégrés pour une sécurite maximale"
              />
            </div>
          </div>

          <div className="mt-20 grid gap-8 lg:grid-cols-3">
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                highlighted={index === 4}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1f5d3f] px-5 py-20 text-white md:px-10 md:py-28">
        <div className="grid  gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <SectionTag dark>Tableau de bord</SectionTag>
            <h2 className="mt-8 text-3xl md:text-4xl font-black leading-tight ">
              Tout votre transport scolaire, <span className="text-[#d9a221]">en un coup d'oeil</span>
            </h2>
            <p className="mt-8 max-w-[560px] text-xl leading-10 text-white/78">
              Notre tableau de bord intuitif vous offre une vue complete de votre flotte. Carte en temps reel,
              alertes, statistiques et rapports, tout est centralise.
            </p>

            <div className="mt-10 space-y-5">
              {[
                "Carte interactive en temps reel",
                "Alertes et notifications instantanees",
                "Rapports detailles et statistiques",
              ].map((item) => (
                <div key={item} className="flex items-center gap-4 text-xl">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#d9a221] text-[#d9a221]">
                    <FiCheckCircle />
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[26px] bg-[#021d10] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
          <img src={dashboardTracking} alt="Tableau de bord Lewoo Watch Kids" className="w-full rounded-[22px] object-cover"/>
          </div>

        </div>
      </section>

      <section id="comment-ca-marche" className="bg-[#06111d] px-5 py-24 text-white md:px-10 md:py-28">
        <div className="mx-auto ">
          <div className="text-center">
            <SectionTag dark>Comment ca marche</SectionTag>
            <div className="mt-8">
              <SectionHeading title="Operationnel en 4 etapes simples" light />
            </div>
          </div>

          <div className="mt-20 space-y-16">
            {steps.map((step, index) => (
              <div key={step.number} className={`grid gap-12 lg:grid-cols-2 lg:items-center ${index % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="relative overflow-hidden rounded-[28px] bg-[#032412] p-4">
                  <span className="absolute left-6 top-6 z-10 flex h-16 w-16 items-center justify-center rounded-full bg-[#d9a221] text-2xl font-black text-[#11452d]">
                    {step.number}
                  </span>
                  <img src={step.image} alt={step.title} className="h-[280px] w-full rounded-[24px] object-cover md:h-[340px]" />
                </div>
                <div>
                  <h3 className="text-3xl font-black md:text-5xl">{step.title}</h3>
                  <p className="mt-6 max-w-[500px] text-xl leading-10 text-white/72">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-24 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1120px] gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <div className="relative">
            <div className="absolute left-4 top-4 rounded-xl bg-[#d9a221] px-4 py-3 text-sm font-bold text-[#11452d] shadow-lg">
              Votre enfant est monte dans le bus !
            </div>
            <img src={parentNotification} alt="Application parents" className="w-full rounded-[28px] object-cover shadow-[0_18px_40px_rgba(0,0,0,0.12)]" />
          </div>

          <div>
            <SectionTag>Parents en direct</SectionTag>
            <h2 className="mt-8 font-black leading-tight md:text-3xl">
              Des parents informés, des enfants 
              <br />
             <span className="text-[#d9a221]">protégés</span>
            </h2>
            <p className="mt-8 text-xl leading-10 text-[#55657a]">
              Recevez des notifications push a chaque etape du trajet de votre enfant. Montee, descente, retards:
              restez informes en permanence.
            </p>

            <div className="mt-4 space-y-5">
              {parentBenefits.map((item, index) => (
                <div key={item} className="flex items-center gap-4 rounded-2xl">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e4e8e3] text-2xl text-[#1f5d3f]">
                    {index === 0 ? <FiBell /> : index === 1 ? <FiClock /> : index === 2 ? <FiShield /> : <FiGrid />}
                  </span>
                  <span className="text-xl text-[#07111b]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden px-5 py-28 text-white md:px-10"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(3,10,18,0.88) 0%, rgba(3,10,18,0.66) 40%, rgba(3,10,18,0.84) 100%), 
          url(${controlRoom})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="mx-auto ">
          <SectionTag dark>Salle de controle</SectionTag>
          <h2 className="mt-8 text-5xl font-black md:text-7xl">
            Supervision <span className="text-[#d9a221]">24h/24</span>
          </h2>
          <p className="mt-8 max-w-[760px] text-xl leading-10 text-white/78">
            Notre centre de controle surveille votre flotte en permanence. Chaque anomalie est detectee et traitee en
            temps reel par nos equipes.
          </p>
        </div>
      </section>

      <section id="infographie" className="px-5 py-24 md:px-10 md:py-28">
        <div className="mx-auto  text-center">
          <SectionTag>Vue d'ensemble</SectionTag>
          <div className="mt-8">
            <SectionHeading title="Architecture de la plateforme" />
          </div>
          <div className="mt-16 overflow-hidden rounded-[28px] bg-white p-4 shadow-[0_14px_36px_rgba(0,0,0,0.08)]">
            <img src={lewooInfographic} alt="Architecture Lewoo Watch Kids" className="w-full rounded-[22px] object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[#1f5d3f] px-5 py-24 text-white md:px-10 md:py-28">
        <div className="mx-auto  text-center">
          <SectionTag dark>Temoignages</SectionTag>
          <div className="mt-6">
            <SectionHeading title="Ils nous font confiance" light />
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {testimonials.map((item) => (
              <div key={item.name} className="rounded-[22px] border border-white/12 bg-white/10 p-7 text-left">
                <div className="text-2xl tracking-[0.3em] text-[#d9a221]">*****</div>
                <p className="mt-6 text-xl italic leading-9 text-white/90">"{item.quote}"</p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#d9a221] font-black text-[#11452d]">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-xl font-black">{item.name}</div>
                    <div className="text-white/60">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="px-5 py-20 md:px-10 md:py-24">
        <div className="mx-auto ">
          <div className="mb-16 flex flex-wrap items-center justify-center gap-6 text-center text-xs font-semibold uppercase tracking-[0.28em] text-[#66758a]">
            {partners.map((partner) => (
              <span key={partner} className="rounded-md border border-[#d6d9dd] bg-white px-5 py-4 tracking-[0.08em] normal-case text-sm font-medium text-[#66758a]">
                {partner}
              </span>
            ))}
          </div>

          <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
            <div>
              <h2 className="font-black leading-tight md:text-4xl">
                Pret a securiser le transport de vos eleves ?
              </h2>
              <p className="mt-8 max-w-[520px] text-lg leading-9 text-[#66758a]">
                Demandez une demonstration gratuite et decouvrez comment Lewoo Watch Kids peut transformer la gestion
                de votre flotte scolaire.
              </p>

              <div className="mt-10 space-y-5 text-lg">
                <div className="flex items-center gap-4 text-[#1f5d3f]">
                  <FiPhone />
                  <span>+237 6XX XXX XXX</span>
                </div>
                <div className="flex items-center gap-4 text-[#1f5d3f]">
                  <FiMail />
                  <span>contact@lewoo.cm</span>
                </div>
              </div>
            </div>

            <form className="space-y-4">
              <input className="w-full rounded-lg border border-[#cfd5db] bg-white px-4 py-4 outline-none" placeholder="Nom complet" />
              <input className="w-full rounded-lg border border-[#cfd5db] bg-white px-4 py-4 outline-none" placeholder="Adresse email" />
              <input className="w-full rounded-lg border border-[#cfd5db] bg-white px-4 py-4 outline-none" placeholder="Telephone" />
              <select className="w-full rounded-lg border border-[#cfd5db] bg-white px-4 py-4 outline-none">
                <option>Type d'etablissement</option>
                <option>Ecole primaire</option>
                <option>College</option>
                <option>Lycee</option>
              </select>
              <textarea
                className="min-h-[140px] w-full rounded-lg border border-[#cfd5db] bg-white px-4 py-4 outline-none"
                placeholder="Votre message"
              />
              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-3 rounded-lg bg-[#d9a221] px-6 py-5 text-sm font-black uppercase tracking-[0.04em] text-[#07111b]"
              >
                Envoyer la demande <FiArrowRight />
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="bg-[#06111d] px-5 py-10 text-white md:px-10">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Lewoo Lite" className="h-8 w-8 rounded bg-white object-contain p-1" />
            <span className="font-bold">Lewoo Lite</span>
          </div>
          <div className="text-sm text-white/50">(c) 2026 Lewoo Lite - Ministere des Transports du Cameroun. Tous droits reserves.</div>
        </div>
        <div className="mx-auto mt-4 max-w-[1120px] text-center text-xs text-white/30">
          Plateforme nationale de suivi du transport scolaire - Yaounde, Cameroun
        </div>
      </footer>
    </div>
  );
}
